import React from "react";
import { Link } from "react-router-dom";
import { CircleDot, Clock, Moon, Sun, Utensils } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import { EDITORIAL } from "../../lib/data";
import { PHOTO_BRIEF } from "../../lib/contentImagePlaceholders";
import WhyHerbBasedNote from "../../components/content/WhyHerbBasedNote";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";

const shakeCards = [
  {
    title: "Protein shake",
    text: "Stir or blend a spoonful into your usual shake. It fits naturally into the same routine: simple, scoopable, and easy to add to your day. Not a replacement for protein powder; just a simple stir-in.",
  },
  {
    title: "Coconut water",
    text: "Shake with coconut water for a light, mineral note. Keep gel cold until you mix, then drink soon after.",
  },
  {
    title: "Dairy-free milk",
    text: "Oat, almond, coconut, or soy: whisk in off the boil for a latte-style sip without overpowering the milk.",
  },
  { title: "Oats", text: "Fold in after cooking so heat stays gentle on the gel." },
  { title: "Tea", text: "Whisk into tea cooled slightly off the boil for a silky finish." },
  {
    title: "Smoothie at home",
    text: "Blend into a drink you build in your own blender (fruit or greens only if you already use them there). Our shipped jars stay herb-based; your bowl stays yours at home.",
  },
];

const ideas = [
  { title: "Smoothies at home", text: "Blend a spoonful with fruit, greens, or protein in your kitchen blender when you want a fuller drink. Shipped MossMatrix custom gels stay botanical; extras like fruit stay on your counter." },
  { title: "Tea", text: "Whisk into tea that’s cooled slightly off the boil for a silky finish." },
  { title: "Oats / porridge", text: "Stir in after cooking so heat stays gentle on the gel." },
  { title: "Juices", text: "Shake with citrus and ginger for a bright sip you mix yourself." },
  { title: "Soups & sauces", text: "Fold in at the end for body without dairy." },
  {
    title: "Skincare mask (optional)",
    text: "Some people patch-test a thin layer. Stop if skin complains. Not a substitute for dermatology advice.",
  },
];

const rituals = [
  {
    title: "Morning",
    text: "Half a spoonful in citrus juice or oats. Start small and see how your palate responds.",
  },
  {
    title: "Afternoon",
    text: "Blend into a post-lunch smoothie you make at home when you want something quiet between meetings.",
  },
  {
    title: "Evening",
    text: "Stir into warm golden milk or chamomile tea off the boil. Cosy, not clinical.",
  },
];

export default function HowToUseSeaMossGelPage() {
  const path = "/learn/how-to-use-sea-moss-gel";
  return (
    <SiteShell theme="dark">
      <SEO
        title="How to use sea moss gel | At-home drinks, tea, oats and more"
        description="How to use MossMatrix sea moss gel at home: cooled teas, oats, juices, sauces, shakes you blend yourself, optional skincare patch tests, and simple ritual ideas. Start small; no medical dosing."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn"
        title="How to use sea moss gel without turning it into homework."
        subtitle="Think of it as a simple stir-in for drinks and bowls you make at home. Many people land on a small spoonful or two when they want consistency. That is not a prescription, just what we hear from folks who like a steady rhythm."
      >
        <div className="max-w-3xl mb-10 space-y-6">
          <p className="text-sm text-botanical/85 leading-relaxed">
            Sea moss gel can be stirred or blended into protein shakes, smoothies, coconut water, coconut milk, oat milk, almond milk, dairy-free
            milks, cow’s milk if you choose, teas, juices, oats, or recipes at home. Fruit belongs in your kitchen blender, not in our shipped
            jars. We keep MossMatrix made-to-order blends{" "}
            <strong className="text-forest font-medium">herb-based with powdered or prepared botanicals only</strong> for freshness, stability,
            and safer UK delivery.
          </p>
          <WhyHerbBasedNote />
          <WellnessDisclaimer />
        </div>

        <div className="rounded-[2rem] overflow-hidden border border-cream mb-10 max-w-4xl">
          <img src={EDITORIAL.smoothieScene} alt="" className="w-full aspect-[21/9] object-cover editorial-img" />
          <p className="px-4 py-2 text-[10px] text-botanical/50 bg-offwhite">{PHOTO_BRIEF.smoothiePrep}</p>
        </div>

        <div className="rounded-2xl border border-gold/30 bg-cream/50 p-6 mb-12 max-w-3xl">
          <h2 className="font-serif text-lg text-forest flex items-center gap-2">
            <CircleDot className="w-4 h-4 text-gold/90 shrink-0" strokeWidth={1.6} aria-hidden />
            <span>Start small</span>
          </h2>
          <p className="mt-2 text-sm text-botanical/85 leading-relaxed">
            Taste first, scale second. Sea moss can add variety to what you eat at home. It does not replace hydration, protein, sleep, or care from professionals when you need it.
          </p>
        </div>

        <h2 className="font-serif text-2xl text-forest mb-2 flex items-center gap-2 flex-wrap">
          <Utensils className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>Add it to your shake</span>
        </h2>
        <p className="text-sm text-botanical/75 max-w-3xl mb-6 leading-relaxed">
          Sea moss gel can be stirred or blended into protein shakes, smoothies, coconut water, coconut milk, or your usual morning drink. It fits
          naturally into the same routine. Simple and easy to add, without comparing it to creatine, whey, or any supplement “performance.”
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {shakeCards.map((c) => (
            <div key={c.title} className="rounded-2xl border border-cream bg-white p-5">
              <h3 className="font-serif text-lg text-forest flex items-center gap-2">
                <Utensils className="w-4 h-4 text-gold/85 shrink-0" strokeWidth={1.5} aria-hidden />
                <span>{c.title}</span>
              </h3>
              <p className="mt-2 text-sm text-botanical/85 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>

        <h2 className="font-serif text-2xl text-forest mb-6 flex items-center gap-2 flex-wrap">
          <Clock className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>Morning · Afternoon · Evening</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {rituals.map((r) => {
            const RitIcon = r.title === "Morning" ? Sun : r.title === "Evening" ? Moon : Clock;
            return (
              <div key={r.title} className="rounded-2xl border border-cream bg-white p-5">
                <h3 className="font-serif text-xl text-forest flex items-center gap-2">
                  <RitIcon className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
                  <span>{r.title}</span>
                </h3>
                <p className="mt-2 text-sm text-botanical/85 leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>

        <h2 className="font-serif text-2xl text-forest mb-6 flex items-center gap-2 flex-wrap">
          <Utensils className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>Ideas by format</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {ideas.map((i) => (
            <div key={i.title} className="rounded-2xl border border-cream bg-offwhite p-5">
              <h3 className="font-serif text-lg text-forest flex items-center gap-2">
                <Utensils className="w-4 h-4 text-gold/85 shrink-0" strokeWidth={1.5} aria-hidden />
                <span>{i.title}</span>
              </h3>
              <p className="mt-2 text-sm text-botanical/85 leading-relaxed">{i.text}</p>
            </div>
          ))}
        </div>

        <ConversionCtas />

        <p className="text-sm text-botanical/80 text-center mt-10">
          <Link to="/learn/sea-moss-for-active-routines" className="underline hover:text-forest">
            Active routines
          </Link>
          {" · "}
          <Link to="/learn/how-to-store-sea-moss-gel" className="underline hover:text-forest">
            Storage guide
          </Link>
          {" · "}
          <Link to="/learn" className="underline hover:text-forest">
            Learn hub
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">
            Shop
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">Choose your sea moss</Link>{" � "}<Link to="/mix-ins" className="underline hover:text-forest">Explore mix-in ideas</Link>
          {" · "}
          <Link to="/ingredients" className="underline hover:text-forest">
            Ingredients
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
